import React, { useState, useEffect } from 'react';
import { useFileContext } from '../../ContextAPI/FileContext';
import './ToggleSwitch.css';
import { ManagerlApi } from '../../API/api';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { FaTrash } from 'react-icons/fa';

const ExistentGuardrailsTab = () => {
  const {
    isExistentGuardrailsEnabled,
    setIsExistentGuardrailsEnabled,
    setRunExistentGuardrails,
  } = useFileContext();

  // ── FORM STATE ────────────────────────────────────────────────────────────
  const [folderPath, setFolderPath] = useState('');
  const [outputFolder, setOutputFolder] = useState('');
  const [dbName, setDbName] = useState('');
  const [step, setStep] = useState('');
  const [pathId, setPathId] = useState('');

  // ── UI STATE ──────────────────────────────────────────────────────────────
  const [showCheckMark, setShowCheckMark] = useState(false);
  const [currentFolderId, setCurrentFolderId] = useState('');
  const [requests, setRequests] = useState([]);

  // ── HELPERS ───────────────────────────────────────────────────────────────
  const resetForm = () => {
    setFolderPath('');
    setOutputFolder('');
    setDbName('');
    setStep('');
    setPathId('');
  };

  const extractFolderId = (url) => {
    const parts = url.trim().split('/');
    return parts[parts.length - 1];
  };

  const getFormattedTime = () => {
    const now = new Date();
    return now.toLocaleTimeString('en-GB', { hour12: false });
  };
  // ── MAIN ACTION ───────────────────────────────────────────────────────────
  const handleRunExistentGuardrails = async () => {
    const folderId = extractFolderId(folderPath);
    if (!folderId) {
      console.error('Invalid folder URL');
      return;
    }

    const apiUrl = `${ManagerlApi}/existent_guardrails/${folderId}`;

    // Visual feedback
    setCurrentFolderId(folderId);
    setShowCheckMark(false);
    setTimeout(() => {
      setShowCheckMark(true);
      setTimeout(() => setShowCheckMark(false), 6000);
    }, 100);

    const startTime = getFormattedTime();
    const requestId = uuidv4();
    setRequests((prev) => [...prev, { id: requestId, folderId, status: 'Sent', startTime }]);
    const numericStep = step === '' ? undefined : step;
    // Build JSON payload from user inputs
    const payload = {
      output_folder: outputFolder,
      db_name: dbName,
      step: numericStep,
      path_id: pathId,
    };

    try {
      const { data } = await axios.post(apiUrl, payload, {
        headers: { 'Content-Type': 'application/json' },
      });
      const statusMessage = data?.message || 'Task Completed';
      setRequests((prev) =>
        prev.map((r) => (r.id === requestId ? { ...r, status: `${statusMessage} ✅` } : r))
      );
    } catch (error) {
      console.error('Error running existent guardrails:', error);
      const errorMessage = error.response?.data?.message || 'Task Failed';
      setRequests((prev) =>
        prev.map((r) => (r.id === requestId ? { ...r, status: `${errorMessage} ❌` } : r))
      );
    } finally {
      resetForm();
    }
  };

  // ── REQUEST LIST MANIPULATION ─────────────────────────────────────────────
  const handleDeleteRequest = (id) =>
    setRequests((prev) => prev.filter((req) => req.id !== id));
  const handleClearAll = () => setRequests([]);

  // Expose run handler to parent context
  useEffect(() => {
    setRunExistentGuardrails(() => handleRunExistentGuardrails);
  }, [folderPath, outputFolder, dbName, step, pathId, setRunExistentGuardrails]);

  // ── RENDER ────────────────────────────────────────────────────────────────
  return (
    <div>
      

      {/* Input Grid */}
      <div className="input-grid">
        <div>
          <label>Input Folder ID</label>
          <input
            type="text"
            value={folderPath}
            onChange={(e) => setFolderPath(e.target.value)}
          />
        </div>
        <div>
          <label>Output Folder ID</label>
          <input
            type="text"
            value={outputFolder}
            onChange={(e) => setOutputFolder(e.target.value)}
          />
        </div>
        <div>
          <label>DB Name</label>
          <input
            type="text"
            value={dbName}
            onChange={(e) => setDbName(e.target.value)}
          />
        </div>
        <div>
          <label>Step (feet)</label>
          <input
            type="number"
            value={step}
            onChange={(e) =>
              setStep(e.target.value === ''          
                ? ''                              
                : Number(e.target.value))         
            }
          />
        </div>
        <div>
          <label>Path ID (in Path Group)</label>
          <input
            type="text"
            value={pathId}
            onChange={(e) => setPathId(e.target.value)}
          />
        </div>
      </div>

      <button onClick={handleRunExistentGuardrails} >
        Run Existent Guardrails
      </button>

      {showCheckMark && <div className="check-mark">Request {currentFolderId} Sent ✅</div>}

      {/* Requests Table */}
      <div className="request-list">
        <table>
          <thead>
            <tr>
              <th>Request ID</th>
              <th>Status</th>
              <th>Start Time</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {requests.map((request) => (
              <tr key={request.id}>
                <td>{request.folderId}</td>
                <td>{request.status}</td>
                <td>{request.startTime}</td>
                <td>
                  <button onClick={() => handleDeleteRequest(request.id)}>
                    <FaTrash />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button className="clear-all-button" onClick={handleClearAll}>
          Remove All
        </button>
      </div>
    </div>
  );
};

export default ExistentGuardrailsTab;
